import React, { useState, useEffect } from 'react';
import {
  Search, Calendar, ArrowUpRight, ArrowDownLeft, DollarSign,
  Filter, RefreshCw, Plus, Eye, MoreHorizontal, CreditCard, Wallet,
  TrendingUp, TrendingDown, Target, Receipt, FileText, Upload,
  ArrowLeftRight, Building, Clock, CheckCircle, AlertCircle,
  BarChart3, PieChart, Activity, Star, Tag, Globe, Gift, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { customerApi, CustomerTransaction, TransactionListParams, TransactionSummary } from '@/api/customers';
import toast from 'react-hot-toast';

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [transactionSummary, setTransactionSummary] = useState<TransactionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters - Updated to match backend types
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'deposit' | 'bonus' | 'withdrawal' | 'interest_payment' | 'refund'>('all');
  // Durum filtresi kaldırıldı
  const [dateFilter, setDateFilter] = useState('all'); // Default to all times
  // Sıralama kontrolleri kaldırıldı; tarih-azalan kullanılır

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 15;

  // Date range
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState<CustomerTransaction | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch transactions from API
  const fetchTransactions = async () => {
    try {
      setError(null);
      const params: TransactionListParams = {
        page: currentPage,
        page_size: itemsPerPage,
      };

      if (typeFilter !== 'all') params.transaction_type = typeFilter;
      if (dateRange.start_date) params.date_from = dateRange.start_date;
      if (dateRange.end_date) params.date_to = dateRange.end_date;

      const response = await customerApi.transactions.list(params);
      setTransactions(response.transactions);
      setTotalTransactions(response.total);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Laden der Transaktionen';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching transactions:', err);
    }
  };

  // Fetch transaction summary from backend
  const fetchTransactionSummary = async () => {
    try {
      const summary = await customerApi.transactions.getSummary(
        dateRange.start_date || undefined,
        dateRange.end_date || undefined
      );
      setTransactionSummary(summary);
    } catch (err: any) {
      console.error('Error fetching transaction summary:', err);
      // Don't show error toast for summary, just log it
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchTransactions(), fetchTransactionSummary()]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentPage, typeFilter, dateRange.start_date, dateRange.end_date]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchTransactions(), fetchTransactionSummary()]);
      toast.success('Daten wurden aktualisiert');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get transaction type display info
  const getTransactionTypeInfo = (type: string) => {
    const typeMap = {
      deposit: { icon: ArrowDownLeft, color: 'text-primary-700', bg: 'bg-primary-50', label: 'Einzahlung' },
      bonus: { icon: Gift, color: 'text-gold-700', bg: 'bg-gold-50', label: 'Neukundenbonus' },
      withdrawal: { icon: ArrowUpRight, color: 'text-warning-700', bg: 'bg-warning-50', label: 'Auszahlung' },
      interest_payment: { icon: TrendingUp, color: 'text-success-700', bg: 'bg-success-50', label: 'Zinszahlung' },
      investment_creation: { icon: Receipt, color: 'text-primary-700', bg: 'bg-primary-50', label: 'Investition erstellt' },
      investment_maturity: { icon: Receipt, color: 'text-slate-700', bg: 'bg-slate-100', label: 'Investition fällig' },
      penalty: { icon: Receipt, color: 'text-danger-700', bg: 'bg-danger-50', label: 'Strafe' },
      fee: { icon: Receipt, color: 'text-warning-700', bg: 'bg-warning-50', label: 'Gebühr' },
      refund: { icon: ArrowLeftRight, color: 'text-success-700', bg: 'bg-success-50', label: 'Erstattung' },
      investment: { icon: Receipt, color: 'text-slate-700', bg: 'bg-slate-100', label: 'Investition' },
    } as const;

    return typeMap[type as keyof typeof typeMap] || {
      icon: Receipt, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Andere'
    };
  };

  // Get transaction status display info
  const getTransactionStatusInfo = (status: string) => {
    const statusMap = {
      'completed': { color: 'text-success-700', bg: 'bg-success-50', border: 'border-success-200', label: 'Abgeschlossen' },
      'pending': { color: 'text-gold-700', bg: 'bg-gold-50', border: 'border-gold-200', label: 'Ausstehend' },
      'cancelled': { color: 'text-danger-700', bg: 'bg-danger-50', border: 'border-danger-200', label: 'Storniert' },
    };

    return statusMap[status as keyof typeof statusMap] || {
      color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', label: 'Unbekannt'
    };
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter transactions based on search term
  const filteredTransactions = transactions.filter(transaction =>
    transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (transaction.reference_number && transaction.reference_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (transaction.bank_name && transaction.bank_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // İstemci tarafı sıralama kaldırıldı; backend tarih-azalan döner

  // Apply date filter
  useEffect(() => {
    if (dateFilter === 'all') {
      setDateRange({ start_date: '', end_date: '' });
    } else {
      const days = parseInt(dateFilter);
      if (days > 0) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        setDateRange({
          start_date: startDate.toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0]
        });
      } else {
        setDateRange({ start_date: '', end_date: '' });
      }
    }
  }, [dateFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Transaktionen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-1.5 block">Konto</span>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Transaktionshistorie</h1>
          <p className="text-slate-500 text-sm mt-1">Sehen und verwalten Sie alle Ihre Transaktionen</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={refreshData}
            disabled={isRefreshing}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Aktualisieren</span>
          </Button>
        </div>
      </div>

      {/* Transaction Summary Cards */}
      {transactionSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="bg-white border border-slate-200 border-t-2 border-t-success-600 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamt Eingänge</p>
                <p className="text-2xl font-serif font-medium text-success-700 tabular-nums mt-1">
                  {formatCurrency(transactionSummary.total_deposits + transactionSummary.total_interest_payments)}
                </p>
              </div>
              <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
                <ArrowDownLeft className="h-5 w-5 text-success-700" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 border-t-2 border-t-danger-600 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamt Ausgänge</p>
                <p className="text-2xl font-serif font-medium text-danger-700 tabular-nums mt-1">
                  {formatCurrency(transactionSummary.total_withdrawals + transactionSummary.total_fees)}
                </p>
              </div>
              <div className="w-11 h-11 bg-danger-50 flex items-center justify-center shrink-0">
                <ArrowUpRight className="h-5 w-5 text-danger-700" />
              </div>
            </div>
          </div>

          <div className={`bg-white border border-slate-200 border-t-2 p-6 ${transactionSummary.net_amount >= 0 ? 'border-t-primary-600' : 'border-t-danger-600'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nettoänderung</p>
                <p className={`text-2xl font-serif font-medium tabular-nums mt-1 ${transactionSummary.net_amount >= 0 ? 'text-primary-700' : 'text-danger-700'}`}>
                  {formatCurrency(transactionSummary.net_amount)}
                </p>
              </div>
              <div className={`w-11 h-11 flex items-center justify-center shrink-0 ${transactionSummary.net_amount >= 0 ? 'bg-primary-50' : 'bg-danger-50'}`}>
                <DollarSign className={`h-5 w-5 ${transactionSummary.net_amount >= 0 ? 'text-primary-700' : 'text-danger-700'}`} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamt Transaktionen</p>
                <p className="text-2xl font-serif font-medium text-slate-900 tabular-nums mt-1">{transactionSummary.transaction_count}</p>
              </div>
              <div className="w-11 h-11 bg-gold-50 flex items-center justify-center shrink-0">
                <Receipt className="h-5 w-5 text-gold-700" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Transaktion suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex space-x-3">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="all">Alle Typen</option>
                <option value="deposit">Einzahlung</option>
                <option value="bonus">Neukundenbonus</option>
                <option value="withdrawal">Auszahlung</option>
                <option value="interest_payment">Zinszahlung</option>
                <option value="refund">Erstattung</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="all">Alle Zeiträume</option>
                <option value="30">Letzter Monat</option>
                <option value="90">Letzte 3 Monate</option>
                <option value="180">Letzte 6 Monate</option>
                <option value="365">Letztes Jahr</option>
                <option value="730">Letzte 2 Jahre</option>
                <option value="1095">Letzte 3 Jahre</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Typ</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Beschreibung</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Betrag</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Datum</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Referenz</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filteredTransactions.map((transaction) => {
                const typeInfo = getTransactionTypeInfo(transaction.type);
                const statusInfo = getTransactionStatusInfo(transaction.status);
                const TypeIcon = typeInfo.icon;

                return (
                  <tr key={transaction.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${typeInfo.bg}`}>
                          <TypeIcon className={`h-4 w-4 ${typeInfo.color}`} />
                        </div>
                        <span className="ml-3 text-sm font-medium text-slate-900">{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">{transaction.description}</div>
                      {transaction.bank_name && (
                        <div className="text-sm text-slate-500">{transaction.bank_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold tabular-nums ${
                        ['deposit', 'bonus', 'interest_payment', 'refund'].includes(transaction.type)
                          ? 'text-success-700'
                          : 'text-danger-700'
                      }`}>
                        {['deposit', 'bonus', 'interest_payment', 'refund'].includes(transaction.type) ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium border ${statusInfo.bg} ${statusInfo.color} ${statusInfo.border}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                      {formatDate(transaction.created_at || transaction.date || '')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {transaction.reference_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedTransaction(transaction)}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                <Receipt className="h-6 w-6 text-slate-400" />
              </div>
              <h3 className="text-sm font-serif font-medium text-slate-900">Keine Transaktion gefunden</h3>
              <p className="mt-1 text-sm text-slate-500">Keine Transaktion entspricht Ihren Suchkriterien.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-500">
                Anzeige {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalTransactions)} von {totalTransactions} Transaktionen
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1 text-sm"
                >
                  Zurück
                </Button>
                <span className="px-3 py-1 text-sm bg-primary-50 text-primary-700 border border-primary-200 tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 text-sm"
                >
                  Weiter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-serif font-medium text-slate-900">Transaktionsdetails</h3>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Typ</label>
                  <p className="text-sm text-slate-900 mt-0.5">{getTransactionTypeInfo(selectedTransaction.type).label}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Betrag</label>
                  <p className="text-sm text-slate-900 tabular-nums mt-0.5">{formatCurrency(selectedTransaction.amount)}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
                  <p className="text-sm text-slate-900 mt-0.5">{getTransactionStatusInfo(selectedTransaction.status).label}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Beschreibung</label>
                  <p className="text-sm text-slate-900 mt-0.5">{selectedTransaction.description}</p>
                </div>

                {selectedTransaction.reference_number && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Referenznummer</label>
                    <p className="text-sm text-slate-900 mt-0.5">{selectedTransaction.reference_number}</p>
                  </div>
                )}

                {selectedTransaction.bank_name && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bank</label>
                    <p className="text-sm text-slate-900 mt-0.5">{selectedTransaction.bank_name}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Datum</label>
                  <p className="text-sm text-slate-900 mt-0.5">{formatDate(selectedTransaction.created_at || selectedTransaction.date || '')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsPage;
